// src/components/recipient/RecipientMusic.tsx
import { useEffect, useRef, useState, useCallback } from "react";
import { ArchetypeId } from "../../constants/archetypes";
import { FlowStep } from "../../types/recipient.types";

interface RecipientMusicProps {
  archetype: ArchetypeId;
  currentStep: FlowStep;
  isMuted?: boolean;
}

// Volume levels (0-10 scale, converted to 0-1)
const STEP_VOLUME_TARGETS: Record<FlowStep, number> = {
  loading: 6,
  proposal: 10,
  letter: 7,
  bouquet: 10,
  completed: 0,
  "reaction-capture": 4,
};

export const RecipientMusic: React.FC<RecipientMusicProps> = ({
  archetype,
  currentStep,
  isMuted = false,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const fadeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentSrcRef = useRef<string>("");
  const [isTabActive, setIsTabActive] = useState(true);

  const getMusicSource = useCallback((): string => {
    switch (archetype) {
      case "moments":
        return "/music/moments-cinematic-piano.mp3";
      case "heartbeat":
        return "/music/heartbeat-gentle-emotions.mp3";
      case "safehaven":
        return "/music/safehaven-cinematic-cello.mp3";
      default:
        return "/music/heartbeat-gentle-emotions.mp3";
    }
  }, [archetype]);

  // Handle tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === "visible";
      setIsTabActive(isVisible);

      // If tab becomes hidden, immediately mute
      if (!isVisible && audioRef.current) {
        audioRef.current.volume = 0;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Initialize audio - only once
  useEffect(() => {
    const musicSrc = getMusicSource();
    currentSrcRef.current = musicSrc;

    const audio = new Audio(musicSrc);
    audio.loop = true;
    audio.preload = "auto";
    audio.volume = 0;

    const handleCanPlayThrough = () => {
      setIsLoaded(true);
    };

    const handleError = (e: ErrorEvent) => {
      console.error("❌ Audio error:", e);
    };

    audio.addEventListener("canplaythrough", handleCanPlayThrough);
    audio.addEventListener("error", handleError);

    audio.load();
    audioRef.current = audio;

    return () => {
      audio.removeEventListener("canplaythrough", handleCanPlayThrough);
      audio.removeEventListener("error", handleError);

      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }
    };
  }, [getMusicSource]);

  // Handle archetype changes - separate effect
  useEffect(() => {
    if (!audioRef.current) return;

    const newSrc = getMusicSource();
    if (newSrc !== currentSrcRef.current) {
      currentSrcRef.current = newSrc;
      audioRef.current.src = newSrc;
      audioRef.current.load();
      setIsLoaded(false);
    }
  }, [archetype, getMusicSource]);

  // Trigger play on ANY user interaction
  useEffect(() => {
    const playAudio = async () => {
      if (!audioRef.current || userInteracted) return;

      try {
        await audioRef.current.play();
        setUserInteracted(true);
      } catch (err) {
        // Silent fail - user interaction will trigger again
      }
    };

    // Listen for ANY user interaction
    const events = ["click", "touchstart", "keydown", "mousedown", "scroll"];

    const handleInteraction = () => {
      playAudio();
      // Remove listeners after first interaction
      events.forEach((event) => document.removeEventListener(event, handleInteraction));
    };

    events.forEach((event) => document.addEventListener(event, handleInteraction, { once: true }));

    return () => {
      events.forEach((event) => document.removeEventListener(event, handleInteraction));
    };
  }, [userInteracted]);

  // Volume control - separate effect with tab visibility
  useEffect(() => {
    if (!audioRef.current || !isLoaded || !userInteracted) return;

    if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);

    // If tab is not active, set volume to 0
    if (!isTabActive) {
      audioRef.current.volume = 0;
      return;
    }

    const targetVolume = isMuted ? 0 : STEP_VOLUME_TARGETS[currentStep] / 10;
    const currentVolume = audioRef.current.volume;

    if (Math.abs(currentVolume - targetVolume) < 0.01) {
      audioRef.current.volume = targetVolume;
      return;
    }

    const steps = 50;
    const intervalMs = currentStep === "loading" ? 80 : currentStep === "completed" ? 60 : 40;
    const step = (targetVolume - currentVolume) / steps;
    let stepCount = 0;

    fadeIntervalRef.current = setInterval(() => {
      if (!audioRef.current) return;

      stepCount++;
      const newVolume = audioRef.current.volume + step;

      if (
        stepCount >= steps ||
        (step > 0 && newVolume >= targetVolume) ||
        (step < 0 && newVolume <= targetVolume)
      ) {
        audioRef.current.volume = targetVolume;
        clearInterval(fadeIntervalRef.current!);
        fadeIntervalRef.current = null;
      } else {
        audioRef.current.volume = newVolume;
      }
    }, intervalMs);

    return () => {
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }
    };
  }, [currentStep, isLoaded, userInteracted, isMuted, isTabActive]);

  return null;
};
